import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CurrencyEur, WarningOctagon, CheckCircle } from "@phosphor-icons/react";
import { InfoHint } from "./InfoHint";
import { periodFxSources } from "./calculate/fxNeeds";

/**
 * v0.4.21 S5 — Period FX rate inputs.
 *
 * Surfaces an FX rate field for every non-base currency present in
 * the selected ingest runs. Visible the moment the operator selects
 * a run with a non-base `declared_currency` — they should not have
 * to run a failed calc to discover they need a rate.
 *
 * Props:
 *   - selectedRuns: array of ingest_run docs (each may carry
 *     .declared_currency, set at upload).
 *   - targetCurrency: "GBP" | "EUR" — this instance's target. A calc
 *     can need boxes for BOTH at once (see
 *     engine.calc_row_rules.apply_row_fx_flag's docstring) — the
 *     caller (FxRatesSection) renders this component once per target
 *     actually needed, not once per calc.
 *   - rates: { [src: string]: string } controlled state — the SLICE
 *     for this target (periodFxRates[targetCurrency]), not the whole
 *     nested map.
 *   - setRates: (next) => void
 *   - extraCurrencies: string[] — additional source currencies that
 *     must always get an input for THIS target (e.g. per-row
 *     currencies flagged MISSING_EXCHANGE_RATE in the calc but not
 *     declared at run level).
 *
 * Returns null when there are no non-base currencies to enter.
 */
export const PeriodFxRates = ({ selectedRuns, targetCurrency, rates, setRates, extraCurrencies = [], onApply = null, applyBusy = false, title = null, hint = null, shortNote = null }) => {
  const { t } = useTranslation();
  // v0.6 (period_fx_rates two-target fix) — was
  // `regime === "UK_VAT" ? "GBP" : "EUR"` computed internally, which
  // assumed a calc only ever needs ONE target currency. A UK_VAT calc
  // can genuinely need both a GBP box (GB-attributed rows) and a EUR
  // box (cross-border OSS rows) at once — see
  // engine.calc_row_rules.apply_row_fx_flag's docstring. The caller
  // (FxRatesSection) now decides which target this instance renders,
  // and can render this component twice.
  const base = targetCurrency;

  const sources = useMemo(   // A2-467: shared with the page's empty state
    () => periodFxSources(selectedRuns, base, extraCurrencies), [selectedRuns, base, extraCurrencies]);

  const allFilled = useMemo(
    () => sources.every((src) => {
      const n = Number(rates?.[src]);
      return Number.isFinite(n) && n > 0;
    }),
    [sources, rates],
  );

  if (sources.length === 0) return null;

  const set = (code, value) =>
    setRates({ ...(rates || {}), [code]: value });

  return (
    <div
      data-testid="period-fx-rates"
      className="border-t border-slate-200 dark:border-slate-700 px-4 py-3 bg-white dark:bg-slate-900"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] tracking-normal text-slate-500 dark:text-slate-400 flex items-center gap-1">
          <CurrencyEur size={11} weight="bold" />
          {title || t("ui.calculate.fx_rates_title")}
          <InfoHint
            testid="fx-rates-info"
            label={hint || t("ui.calculate.fx_rates_subtitle")}
          />
        </div>
        <a
          href="https://www.gov.uk/government/collections/exchange-rates-for-customs-and-vat"
          target="_blank"
          rel="noreferrer"
          data-testid="fx-rates-hmrc-link"
          className="text-[10px] tracking-normal text-teal-700 dark:text-teal-300 hover:text-teal-700 dark:hover:text-teal-300"
        >
          {t("ui.calculate.fx_rates_link_label")} →
        </a>
      </div>
      {shortNote && (
        <p className="-mt-1 mb-2 text-[10px] text-slate-400 dark:text-slate-500 leading-snug">
          {shortNote}
        </p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {sources.map((src) => {
          const v = rates?.[src] ?? "";
          // v0.6.10 — was `!v`, so a typed "0" (or "-1", "abc") looked
          // exactly like a valid, filled-in entry (teal border, no
          // warning) even though allFilled below already correctly
          // excludes it — the Apply button stayed disabled with a
          // generic "enter a rate for every currency" message and no
          // way to tell which box was actually the problem. Same rule
          // as allFilled's own per-field check now.
          const missing = !(Number.isFinite(Number(v)) && Number(v) > 0);
          return (
            <div key={src} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-3 rounded-xl">
              <label className="text-[10px] tracking-normal text-slate-600 dark:text-slate-300">
                {t("ui.calculate.fx_rate_input_label", { src, base })}
              </label>
              <input
                data-testid={`fx-rate-${src}`}
                value={v}
                inputMode="decimal"
                onChange={(e) => set(src, e.target.value)}
                placeholder={t("ui.calculate.fx_rate_placeholder")}
                className={`mt-1 w-full bg-white dark:bg-slate-800 placeholder:text-slate-400 dark:placeholder:text-slate-500 border px-3 py-2 tabular-nums text-sm rounded-xl ${
                  missing
                    ? "border-amber-600 text-amber-700 dark:text-amber-300"
                    : "border-teal-300 dark:border-teal-700 text-teal-700 dark:text-teal-300"
                }`}
              />
              {missing && (
                <div className="mt-1 text-[10px] text-amber-700 dark:text-amber-300 flex items-center gap-1">
                  <WarningOctagon size={11} weight="bold" />
                  {t("ui.calculate.fx_rate_missing_help", { src })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* v0.5.2 fix — the rate is persisted to workflow state on every
          keystroke (no separate "save"), but operators arriving from the
          "enter exchange rate" review action expect an explicit action.
          Tell them it's saved and give a one-click re-run so they don't
          hunt for the Run-calculation button further down the page. */}
      <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
        <div
          data-testid="fx-rates-autosave-note"
          className="text-[10px] tracking-normal text-slate-500 dark:text-slate-400 flex items-center gap-1"
        >
          {allFilled ? (
            <>
              <CheckCircle size={12} weight="bold" className="text-emerald-600 dark:text-emerald-400" />
              {t("ui.calculate.fx_saved_rerun_hint", {
                defaultValue: "Rate saved automatically — re-run the calculation to apply it.",
              })}
            </>
          ) : (
            <>
              <WarningOctagon size={12} weight="bold" className="text-amber-600 dark:text-amber-400" />
              {t("ui.calculate.fx_enter_all_hint", {
                defaultValue: "Enter a rate for every currency above to continue.",
              })}
            </>
          )}
        </div>
        {onApply && (
          <button
            type="button"
            data-testid="fx-rates-apply-btn"
            disabled={!allFilled || applyBusy}
            onClick={onApply}
            className={`inline-flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-lg transition-colors ${
              !allFilled || applyBusy
                ? "bg-slate-200 dark:bg-slate-800 text-slate-400 dark:text-slate-600 cursor-not-allowed"
                : "bg-teal-600 hover:bg-teal-700 text-white"
            }`}
          >
            {applyBusy
              ? t("ui.calculate.fx_applying", { defaultValue: "Applying…" })
              : t("ui.calculate.fx_save_rerun", { defaultValue: "Save & re-run calculation" })}
          </button>
        )}
      </div>
    </div>
  );
};

// missingFxRates moved to ./calculate/flaggedFx.js (v0.6) — it's pure
// logic with no JSX/React dependency, and living here pulled a
// unit-test-unfriendly UI dependency chain (InfoHint -> popover ->
// "@/lib/utils", a Jest-unresolvable alias) into flaggedFx.js's own
// otherwise dependency-free pure-function module the moment it needed
// to import this function for computeFxMissing.
